import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { open_api_with_cookies } from "../../../Utils/network";
import FilterChip from "../../../components/Chip/FilterChip";
import LoadingOverlay from "../../../components/Loading/LoadingOverlay";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import "./AIFeedback.css";

const dayOptions = ["7 Days", "14 Days", "30 Days", "60 Days", "90 Days"];
const dayMap = {
  "7 Days": 7,
  "14 Days": 14,
  "30 Days": 30,
  "60 Days": 60,
  "90 Days": 90,
};

const AIFeedback = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("30 Days");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const days = dayMap[timeframe];
        const res = await open_api_with_cookies.get(
          `/myburgoai/admin/feedback/?days=${days}`
        );
        setData(res.data);
      } catch (error) {
        console.error("Error fetching AI feedback:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [timeframe]);

  if (isLoading) return <LoadingOverlay />;
  if (!data) return <div className="ai-feedback">Failed to load data.</div>;

  const { summary, period, by_model, by_safety_level, feedback_options_breakdown, daily_trend, recent_feedback } = data;

  const chartData = (daily_trend || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    }),
    thumbs_up: d.thumbs_up || 0,
    thumbs_down: d.thumbs_down || 0,
  }));

  return (
    <div className="ai-feedback">
      <div className="ai-feedback-header">
        <h2>AI Feedback Analytics</h2>
        <div style={{ width: 160 }}>
          <FilterChip
            options={dayOptions}
            selected={timeframe}
            onSelect={setTimeframe}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="ai-feedback-summary">
        <div className="ai-feedback-summary-card">
          <div className="fb-label">Total AI Messages</div>
          <div className="fb-value">
            {(summary.total_ai_messages || 0).toLocaleString()}
          </div>
          <div className="fb-sub">all time</div>
        </div>
        <div className="ai-feedback-summary-card positive">
          <div className="fb-label">Thumbs Up</div>
          <div className="fb-value">{summary.thumbs_up}</div>
          <div className="fb-sub">{period.thumbs_up} in period</div>
        </div>
        <div className="ai-feedback-summary-card negative">
          <div className="fb-label">Thumbs Down</div>
          <div className="fb-value">{summary.thumbs_down}</div>
          <div className="fb-sub">{period.thumbs_down} in period</div>
        </div>
        <div className="ai-feedback-summary-card">
          <div className="fb-label">Satisfaction Rate</div>
          <div className="fb-value">{summary.positive_rate}%</div>
          <div className="fb-sub">
            {summary.feedback_rate}% messages rated
          </div>
        </div>
      </div>

      {/* Daily Chart + Options Breakdown */}
      <div className="ai-feedback-charts">
        <div className="ai-feedback-chart-card">
          <h3>Daily Feedback Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis tick={{ fill: "#666" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(255,255,255,0.95)",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              />
              <Legend />
              <Bar
                dataKey="thumbs_up"
                name="Thumbs Up"
                fill="#05cd99"
                radius={[4, 4, 0, 0]}
              />
              <Bar
                dataKey="thumbs_down"
                name="Thumbs Down"
                fill="#ef4444"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="ai-feedback-chart-card">
          <h3>Feedback Options Used</h3>
          {(feedback_options_breakdown || []).length > 0 ? (
            feedback_options_breakdown.map((opt) => (
              <div key={opt.id} className="fb-breakdown-item">
                <div>
                  <span className="fb-breakdown-label">{opt.text}</span>
                  <div style={{ fontSize: 11, color: "#9197b3" }}>
                    {opt.category} &middot; {opt.feedback_type}
                  </div>
                </div>
                <span className="fb-bar-count">{opt.count}</span>
              </div>
            ))
          ) : (
            <div style={{ color: "#9197b3", fontSize: 14, padding: "20px 0" }}>
              No feedback options used yet.
            </div>
          )}
        </div>
      </div>

      {/* Model & Safety Breakdowns */}
      <div className="ai-feedback-breakdowns">
        <div className="ai-feedback-breakdown-card">
          <h3>Feedback by Model</h3>
          {Object.keys(by_model || {}).length > 0 ? (
            Object.entries(by_model).map(([model, counts]) => {
              const total = (counts.thumbs_up || 0) + (counts.thumbs_down || 0);
              const upPct = total > 0 ? (counts.thumbs_up / total) * 100 : 0;
              return (
                <div key={model} className="fb-breakdown-item">
                  <span className="fb-breakdown-label">{model}</span>
                  <div className="fb-breakdown-bar">
                    <div className="fb-bar-track">
                      <div
                        className="fb-bar-fill-up"
                        style={{ width: `${upPct}%` }}
                      />
                    </div>
                    <span className="fb-bar-count" style={{ color: "#05cd99" }}>
                      {counts.thumbs_up || 0}
                    </span>
                    <span className="fb-bar-count" style={{ color: "#ef4444" }}>
                      {counts.thumbs_down || 0}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ color: "#9197b3", fontSize: 14, padding: "20px 0" }}>
              No model feedback data yet.
            </div>
          )}
        </div>

        <div className="ai-feedback-breakdown-card">
          <h3>Feedback by Safety Level</h3>
          {Object.keys(by_safety_level || {}).length > 0 ? (
            Object.entries(by_safety_level).map(([level, counts]) => {
              const total = (counts.thumbs_up || 0) + (counts.thumbs_down || 0);
              const upPct = total > 0 ? (counts.thumbs_up / total) * 100 : 0;
              return (
                <div key={level} className="fb-breakdown-item">
                  <span className="fb-breakdown-label">
                    {level.charAt(0).toUpperCase() + level.slice(1)}
                  </span>
                  <div className="fb-breakdown-bar">
                    <div className="fb-bar-track">
                      <div
                        className="fb-bar-fill-up"
                        style={{ width: `${upPct}%` }}
                      />
                    </div>
                    <span className="fb-bar-count" style={{ color: "#05cd99" }}>
                      {counts.thumbs_up || 0}
                    </span>
                    <span className="fb-bar-count" style={{ color: "#ef4444" }}>
                      {counts.thumbs_down || 0}
                    </span>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ color: "#9197b3", fontSize: 14, padding: "20px 0" }}>
              No safety-level feedback data yet.
            </div>
          )}
        </div>
      </div>

      {/* Recent Feedback */}
      <div className="ai-feedback-recent">
        <h3>Recent Feedback</h3>
        {(recent_feedback || []).length > 0 ? (
          recent_feedback.map((item) => (
            <div
              key={item.message_id}
              className="fb-recent-item"
              onClick={() => navigate(`/ai/sessions/${item.session_id}`)}
            >
              <div className="fb-recent-header">
                <span className="fb-recent-user">{item.user_name}</span>
                <span className="fb-recent-date">
                  {new Date(item.created).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
              <div className="fb-recent-preview">{item.content_preview}</div>
              <div className="fb-recent-badges">
                <span
                  className={`fb-badge ${
                    item.thumbs_feedback === "thumbs_up"
                      ? "fb-badge-up"
                      : "fb-badge-down"
                  }`}
                >
                  {item.thumbs_feedback === "thumbs_up" ? "👍 Up" : "👎 Down"}
                </span>
                {item.ai_model && (
                  <span className="fb-badge fb-badge-model">
                    {item.ai_model}
                  </span>
                )}
                {item.safety_level && item.safety_level !== "safe" && (
                  <span className="fb-badge" style={{ background: "#fff3e0", color: "#e65100" }}>
                    {item.safety_level}
                  </span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div style={{ color: "#9197b3", fontSize: 14, padding: "20px 0", textAlign: "center" }}>
            No feedback received yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default AIFeedback;
